import axios from "axios";
import { getApiUrl, getApiUrlWithVersion } from "./environmentService";
import { store as reduxStore } from "../store/store";
import { setAccessToken, setUserDetails, setMfaPending, purgeAuthUser, setIsOperatorSession } from "../store/slices/authUserSlice";
import { AuthUser } from "../store/slices/authUserSlice";
import { purgeStore } from "../store/rootReducer";
import { selectUserEmail } from "../store/slices/authUserSlice";
import { jwtDecode } from "jwt-decode";

/**
 * Send a passwordless auth email to the given email address
 * and redirect to the given URL after the user clicks the link
 *
 * This is the equivalent of a sign in (not a sign up)
 */
export const loginWithPasswordlessAuth = async ({
  email,
  redirectUrl,
}: {
  email: string;
  redirectUrl: string;
}) => {
  await axios.post(`${getApiUrl()}/auth/send_passwordless_auth_email`, {
    email,
    redirect_url: redirectUrl,
  });
};

/**
 * Place the token from a redirect URL into the local storage
 *
 * TODO: also fetch id and email from the backend and place them in the local storage
 */
export const fetchAndSetCurrentUser = async () => {
  const accessToken = getAuthUser()?.accessToken;
  const response = await axios.get(`${getApiUrlWithVersion()}/users/me`, {
    headers: { Authorization: accessToken },
  });
  const user = response.data.data.user;
  reduxStore.dispatch(setUserDetails({ email: user.email, organization: user.organization, mfa_enabled: user.mfa_enabled }));
  return user;
};

export const ingestPasswordlessAuthToken = async ({
  accessToken,
}: {
  accessToken: string;
}): Promise<{ mfa_required: boolean }> => {
  // Any failure below must purge the store: the token has to be stored before
  // it can be validated (fetchAndSetCurrentUser reads it from the store), and
  // a malformed or dead token (e.g. for a deleted account) left in the
  // persisted store makes selectAuthenticated return true while every API call
  // 401s — bouncing all pages, including later logins, to "Session timed out"
  try {
    reduxStore.dispatch(setAccessToken(accessToken));
    // take the token from jwt and decode it for the email
    const decoded = jwtDecode(accessToken);
    if ('email' in decoded) {
      reduxStore.dispatch(setUserDetails({email: decoded.email as string}));
    }
    const isOperatorSession = "operator_session" in decoded && !!decoded.operator_session;
    // Set (not just conditionally set true) so a prior operator session's
    // persisted flag can't leak into a normal login.
    reduxStore.dispatch(setIsOperatorSession(isOperatorSession));
    // fetch full user details (including org feature flags) from the API
    const user = await fetchAndSetCurrentUser();

    // Check if MFA is required for this user
    // Skip MFA for operator sessions (admin "login as" / latest editions)
    if (user.mfa_enabled && !isOperatorSession) {
      const { challengeMfa } = await import('./mfaService');
      const { mfa_token } = await challengeMfa();

      // Clear the JWT — user must complete MFA first
      reduxStore.dispatch(purgeAuthUser());
      reduxStore.dispatch(setMfaPending({ mfaToken: mfa_token }));

      return { mfa_required: true };
    }

    return { mfa_required: false };
  } catch (error) {
    reduxStore.dispatch(purgeAuthUser());
    throw error;
  }
};

export const login = async (email: string, password: string) => {
  try {
    const response = await axios.post(`${getApiUrl()}/login`, {
      user: {email, password},
    });

    const responseData = response.data;

    // Check if MFA is required
    if (responseData.mfa_required) {
      reduxStore.dispatch(setMfaPending({ mfaToken: responseData.mfa_token }));
      return { mfa_required: true };
    }

    // Normal login — no MFA
    const {data} = responseData;
    const token = response.headers.authorization;

    reduxStore.dispatch(setUserDetails({ email: data.user.email, organization: data.user.organization, mfa_enabled: data.user.mfa_enabled }));
    reduxStore.dispatch(setAccessToken(token));

    return data;
  } catch (error: any) {
    throw new Error(error.response.data);
  }
};

/**
 * Refresh the access token for the current user using the current access token.
 *
 * TODO: this is not how we should do token refresh logic. See the note in auth_layer_controller.rb
 */
export const refreshToken = async (accessToken: string) => {
  console.log("Refreshing token");
  axios.post(`${getApiUrl()}/auth/refresh_token`, {}, {
    headers: {Authorization: accessToken},
  })
  .then((response) => reduxStore.dispatch(setAccessToken(response.headers.authorization)))
  .then(_ => console.log("Token refreshed"));
};

export const getAuthUser = (): AuthUser => {
  return reduxStore.getState().authUser;
};

export const getCurrentUserEmail = (): string | undefined => {
  return selectUserEmail(reduxStore.getState());
};


// TODO add a refresh token

export const logout = async () => {
  const accessToken = getAuthUser()?.accessToken;
  purgeStore();

  // TODO: logout is broken. Let's fix it.
  await axios
    .delete(`${getApiUrl()}/logout`, {
      headers: {Authorization: accessToken},
    })
    .catch(() => {})
};

export const acceptInvite = async (token: string) => {
  const response = await axios.post(`${getApiUrl()}/auth/accept_invite`,
    { token },
    { headers: getAuthHeaders() }
  );

  return response.data;
};

export const updatePassword = async (newPassword: string, newPasswordConfirmation: string) => {
  const response = await axios.patch(`${getApiUrlWithVersion()}/users/password`,
    {
      new_password: newPassword,
      new_password_confirmation: newPasswordConfirmation,
    },
    { headers: getAuthHeaders() }
  );

  return response.data;
};

const getAuthHeaders = () => ({ Authorization: getAuthUser()?.accessToken });
